import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { Form, Select } from "antd";
import BackButton from "../../components/button/BackButton";
import MySegmented from "../../components/segmented/Segmented";
import {
  GetInvoiceTemplateApi,
  GetInvoiceTemplateListApi,
} from "../../api/request/invoice";
import { SendInvoiceApi } from "../../api/request/service";
import LoadableButton from "../../components/button/LoadableButton";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/sendInvoice";
import Add from "../../assets/Add";
import { v4 as uuidv4 } from "uuid";

const SendInvoice = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [invoiceForm] = Form.useForm();
  const [segmentedValue, setSegmentedValue] = useState("Standard");
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceData, setInvoiceData] = useState([]);
  const [isInvoiceTableLoading, setIsInvoiceTableLoading] = useState(false);

  const fileData = state?.fileData;

  const infoList = [
    { label: "User Name", value: fileData?.user?.name || "--" },
    { label: "Email", value: fileData?.user?.email || "--" },
    { label: "Mobile No", value: fileData?.user?.mobile_no || "--" },
    { label: "Date", value: moment().format("ll") },
    { label: "File Number", value: `#${fileData?.file_number || "--"}` },
    { label: "File Name", value: fileData?.service_name_slug || "--" },
  ];

  const totalCharge = invoiceData?.reduce((sum, item) => sum + item?.charge, 0);

  const getInvoiceList = async () => {
    try {
      const { data } = await GetInvoiceTemplateListApi();
      if (data?.status) {
        setInvoiceList(
          data?.data?.map((item) => {
            return {
              value: item?.id,
              label: item?.name,
            };
          })
        );
      }
    } catch (error) {}
  };

  const getInvoiceData = async () => {
    setIsInvoiceTableLoading(true);
    try {
      const { data } = await GetInvoiceTemplateApi({
        invoice_template_id: selectedInvoice,
      });
      if (data?.status) {
        const charges = JSON.parse(data?.data?.title || []).map((item) => ({
          ...item,
          id: uuidv4(),
          unit: item?.unit || 1,
          amount: Number(item?.charge),
          charge: (item?.unit || 1) * Number(item?.charge || 0),
        }));
        setInvoiceData(charges);
      }
    } catch (error) {
    } finally {
      setIsInvoiceTableLoading(false);
    }
  };

  const handleAddCharge = () => {
    setInvoiceData([
      ...invoiceData,
      {
        id: uuidv4(),
        title: "",
        description: "",
        unit: 1,
        amount: 0,
        charge: 0,
      },
    ]);
  };

  const handleAction = (action, record, data) => {
    if (action === "delete") handleDeleteCharge(record);
    else if (action === "edit") handleEditCharge(record, data);
  };

  const handleEditCharge = (record, data) => {
    const updatedInvoice = invoiceData?.map((item) => {
      if (item?.id === record?.id) return { ...item, ...data };
      return item;
    });
    setInvoiceData(updatedInvoice);
  };

  const handleDeleteCharge = (record) => {
    const updatedInvoice = invoiceData?.filter(
      (item) => item?.id !== record?.id
    );
    setInvoiceData(updatedInvoice);
  };

  const sendInvoice = async () => {
    setIsInvoiceTableLoading(true);
    try {
      const { data } = await SendInvoiceApi({
        file_id: id,
        invoice_temp: invoiceData,
      });

      if (data?.status) {
        toast.success(data?.message);
        navigate(`/file/${state?.fileTypeId}/${id}`);
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsInvoiceTableLoading(false);
    }
  };

  useEffect(() => {
    if (id) getInvoiceList();
  }, [id]);

  useEffect(() => {
    if (selectedInvoice) getInvoiceData();
  }, [selectedInvoice]);

  useEffect(() => {
    invoiceForm.resetFields();
    setSelectedInvoice(null);
    setInvoiceData([]);
  }, [segmentedValue]);

  return (
    <div className="bg-white border rounded-xl p-5">
      <div className="flex md:flex-row flex-col justify-between gap-5 mb-5">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="title">Send Invoice</h2>
        </div>
        <MySegmented
          options={["Standard", "Customize"]}
          value={segmentedValue}
          setValue={setSegmentedValue}
        />
      </div>

      <div className="border rounded-xl p-5">
        <div className="grid grid-cols-2 gap-5">
          {infoList?.map(({ label, value }, index) => (
            <div key={index} className="flex items-center gap-3">
              <p className="font-bold">{label}:</p>
              <p>{value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-5 space-y-5">
        <Form
          autoComplete="off"
          layout="vertical"
          form={invoiceForm}
          initialValues={{ invoice_temp_id: null }}
          onFinish={sendInvoice}
        >
          {segmentedValue === "Standard" && (
            <Form.Item
              name="invoice_temp_id"
              label="Select Standard Invoice Template"
              className="mb-0"
              rules={[{ required: true, message: "Please select invoice!" }]}
            >
              <Select
                placeholder="Select Invoice"
                options={invoiceList}
                size="large"
                onChange={(value) => setSelectedInvoice(value)}
                className="!w-[250px]"
              />
            </Form.Item>
          )}
        </Form>

        {(segmentedValue === "Standard" && selectedInvoice) ||
        segmentedValue === "Customize" ? (
          <>
            <div className="space-y-3">
              <SkeletonTable
                columns={columns(handleAction)}
                data={invoiceData}
                isLoading={isInvoiceTableLoading}
              />

              <div className="flex items-center justify-between mt-5">
                <button
                  type="button"
                  className="btn-primary !px-4 !text-sm flex items-center gap-2"
                  onClick={handleAddCharge}
                >
                  <Add />
                  <span>Add Charge</span>
                </button>

                <p className="text-xl font-semibold">Total: ${totalCharge}</p>
              </div>
            </div>

            <div className="mt-5 flex gap-5">
              <LoadableButton
                lable="Submit"
                onClick={() => invoiceForm.submit()}
                loading={isInvoiceTableLoading}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SendInvoice;
